import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import TopNav from './TopNav';
import CosignInboxBanner from '@/components/clinical/CosignInboxBanner';
import useAuthStore from '@/stores/auth';
import { loadPracticeBrand } from '@/utils/practiceBrand';

// Clinical routes get full-bleed (no padding) — the SOAP editor needs every pixel
const FULL_BLEED_ROUTES = ['/clinical', '/encounter'];
// Routes that manage their OWN scrolling (calendar grid scrolls inside a
// fixed viewport, side panel stays pinned). Without this, <main>'s
// overflow-y-auto scrolled the whole page, so opening an afternoon
// appointment meant scrolling back up to read the patient panel
// (Michael Rivera 2026-07-15 — wanted the Google Calendar behavior).
const VIEWPORT_LOCKED_ROUTES = ['/schedule'];

// Routes where the app TopNav + bottom status bar are HIDDEN. Used
// for authoring surfaces that need every pixel of screen real estate.
// /clinical was on this list originally for the SOAP editor but
// Daniel asked to keep the nav visible on it (front desk needs to
// hop from a note to the schedule / front-desk tasks without using
// the back button). /encounter (iPad ambient capture) stays
// chromeless — that's a dedicated PWA-style screen.
const CHROMELESS_ROUTES = ['/encounter'];

export default function AppShell() {
  const location = useLocation();
  const [activationRefusalSession, setActivationRefusalSession] = useState(null);
  useEffect(() => {
    const onRefusal = (event) => {
      if (event.detail?.session === localStorage.getItem('cervica_auth_session')) {
        setActivationRefusalSession(event.detail.session);
      }
    };
    window.addEventListener('cervica:activation-required', onRefusal);
    return () => window.removeEventListener('cervica:activation-required', onRefusal);
  }, []);

  // Warm the practice-brand cache so template builders that can't await
  // (note print letterheads) have it synchronously by print time.
  useEffect(() => { loadPracticeBrand(); }, []);
  const isFullBleed = FULL_BLEED_ROUTES.some((r) =>
    location.pathname === r || location.pathname.startsWith(r + '/')
  );
  const isViewportLocked = VIEWPORT_LOCKED_ROUTES.some((r) =>
    location.pathname === r || location.pathname.startsWith(r + '/')
  );
  const isChromeless = CHROMELESS_ROUTES.some((r) =>
    location.pathname === r || location.pathname.startsWith(r + '/')
  );

  // Staging banner — appears on EVERY screen (including chromeless
  // ones) so a CA / doctor who lands on the wrong URL can't miss it.
  // Runtime presentation config permits the identical image in staging/prod.
  // Hidden for the sales-demo tenant (Daniel 2026-08-25) — a prospect
  // screen-share must not announce "not production". Real-tenant staging
  // sessions keep the warning.
  const tenant = useAuthStore((st) => st.tenant);
  const isDemoTenant = tenant?.slug === 'summit-ridge-demo';
  const isStaging = (window.__CERVICA_ENV__ === 'staging' || window.location.hostname === 'staging.cervicaehr.com' || import.meta.env.VITE_APP_ENV === 'staging') && !isDemoTenant;

  // Billing state — mirrors the server gate in middleware/tenantContext.js
  // (writes 402 when locked; reads stay open). Quiet until the last week
  // of trial, then a countdown; read-only notice once expired or when the
  // Stripe subscription is canceled; amber warning during dunning.
  const isTrial = tenant?.billing_plan === 'trial' && !tenant?.is_demo && tenant?.trial_ends_at;
  const trialDaysLeft = isTrial
    ? Math.ceil((new Date(tenant.trial_ends_at).getTime() - Date.now()) / 86400000)
    : null;
  const subscriptionCanceled = tenant?.billing_status === 'canceled' && !tenant?.is_demo;
  const paymentPastDue = tenant?.billing_status === 'past_due' && !tenant?.is_demo;
  const trialExpired = isTrial && trialDaysLeft <= 0 && !subscriptionCanceled;
  const trialEndingSoon = isTrial && !trialExpired && !subscriptionCanceled && trialDaysLeft <= 7;
  const activateLink = (
    <Link to="/settings#tab=subscription" className="underline underline-offset-2 hover:opacity-80">
      Activate now
    </Link>
  );

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#FAFAFA]">
      {tenant?.customer_activation?.trial_started_at && tenant.customer_activation.state !== 'live' && (
        <div className="shrink-0 bg-amber-100 px-4 py-2 text-center text-sm text-amber-950" role="status">
          {activationRefusalSession && activationRefusalSession === localStorage.getItem('cervica_auth_session')
            ? 'Trial access is paused or has ended. Unsaved changes have not been confirmed; copy any fictional text you want to keep before leaving this page.'
            : 'Fictional-data trial · No real patients, messages or charges.'}{' '}
          <Link to="/onboarding" className="font-semibold underline">Trial and activation details</Link>
        </div>
      )}
      {isStaging && (
        <div className="flex items-center justify-center gap-2 bg-yellow-400 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-yellow-900">
          <span aria-hidden>⚠</span>
          STAGING — not production · external integrations stubbed · do not enter real patient data
          <span aria-hidden>⚠</span>
        </div>
      )}
      {trialExpired && (
        <div className="flex items-center justify-center gap-2 bg-red-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
          Trial ended. Your account is read-only and your data is intact. {activateLink}
        </div>
      )}
      {subscriptionCanceled && (
        <div className="flex items-center justify-center gap-2 bg-red-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
          Subscription ended. Your account is read-only and your data is intact. {activateLink}
        </div>
      )}
      {paymentPastDue && (
        <div className="flex items-center justify-center gap-2 bg-amber-400 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-900">
          Payment issue on your subscription. Update your card in{' '}
          <Link to="/settings#tab=subscription" className="underline underline-offset-2 hover:opacity-80">Settings, Subscription</Link>
        </div>
      )}
      {trialEndingSoon && (
        <div className="flex items-center justify-center gap-2 bg-amber-400 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-900">
          {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left in your free trial. {activateLink}
        </div>
      )}
      {!isChromeless && <TopNav />}
      {/* Viewport lock is DESKTOP-ONLY: on phones the inner flex
          containers don't reliably take over touch scrolling (Chris
          2026-07-15: calendar stuck before lunch on mobile), so small
          screens keep normal page scroll and lose only the pinned
          side panel, which is collapsed on mobile anyway. */}
      <main className={`flex-1 ${isViewportLocked ? 'overflow-y-auto sm:overflow-hidden sm:flex sm:flex-col' : 'overflow-y-auto'} ${isFullBleed ? '' : 'p-2 sm:p-4 lg:p-6'}`}>
        {/* Co-sign inbox surface — visible on every chromed page so
            reviewers never miss a pending cosign. Self-hides when
            empty. Polls every 60s and on window focus. Excluded from
            chromeless authoring routes (clinical editor / encounter)
            so the SOAP editor stays full-bleed. */}
        {!isChromeless && (
          <div className="mb-2 sm:mb-4">
            <CosignInboxBanner />
          </div>
        )}
        {/* Viewport-locked pages (schedule) must constrain their internal
            scroll areas. On desktop <main> is a flex column, so the page
            wrapper takes the REMAINING space (flex-1 min-h-0) after the
            cosign banner — using h-full here made the wrapper 100% of main
            while the banner pushed it down, clipping its bottom off-screen
            so the patient panel couldn't scroll to the end on short
            windows (Daniel 2026-07-21). Mobile keeps h-full + page scroll
            (Chris 2026-07-15 / Ryan 2026-07-16). */}
        <div key={location.pathname} className={`page-enter ${isViewportLocked ? 'h-full sm:h-auto sm:min-h-0 sm:flex-1' : ''}`}>
          <Outlet />
        </div>
      </main>
      {/* Bottom status bar — hidden on chromeless authoring routes. */}
      {!isChromeless && (
        <div className="hidden h-7 items-center justify-end border-t border-gray-200 bg-gray-50 px-4 text-[10px] text-gray-500 sm:flex">
          <span>Cervica EHR v0.1.0</span>
        </div>
      )}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            fontSize: '13px',
            borderRadius: '8px',
            padding: '12px 16px',
            boxShadow: '0 10px 15px rgba(0,0,0,0.04), 0 4px 6px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.05)',
          },
          success: {
            iconTheme: { primary: '#0d9488', secondary: '#fff' },
          },
        }}
      />
    </div>
  );
}
